/* Confirm email-change link landing page. Reads ?token=X, POSTs to confirm, redirects to /login. */
import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Mail, ArrowLeft } from "lucide-react";

export default function ConfirmEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [status, setStatus] = useState("pending");     // pending | ok | error
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!token) { setStatus("error"); setDetail("Missing token"); return; }
    (async () => {
      try {
        const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const r = await fetch(`${backend}/api/auth/change-email/confirm`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Verification failed");
        setStatus("ok");
        setDetail(d.new_email || null);
      } catch (e) {
        setStatus("error");
        setDetail(e.message || "Verification failed");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="confirm-email-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
            <Mail className="text-primary" size={18} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-primary">Zaroori Jini OMS</div>
            <div className="text-lg font-black">Email change confirmation</div>
          </div>
        </div>

        {status === "pending" && (
          <div className="text-sm text-muted-foreground py-6 text-center">Verifying your link…</div>
        )}
        {status === "ok" && (
          <div className="rounded-lg border border-success/40 bg-success/5 p-4 flex items-start gap-3" data-testid="confirm-ok">
            <CheckCircle2 className="text-success shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <b>Email updated.</b><br />
              <span className="text-muted-foreground">Your login email is now <b>{detail}</b>. Please sign in with this new address.</span>
            </div>
          </div>
        )}
        {status === "error" && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 flex items-start gap-3" data-testid="confirm-error">
            <XCircle className="text-destructive shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <b>Verification failed.</b><br />
              <span className="text-muted-foreground">{detail}</span>
            </div>
          </div>
        )}
        <div className="mt-6 flex items-center gap-2">
          {status === "ok" && (
            <Button size="sm" onClick={() => nav("/login")} data-testid="go-signin">Go to sign in</Button>
          )}
          <Link to="/login" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 ml-auto">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
